import { useContext, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { AuthContext } from "../contexts/AuthContext";
import LoginDialog from "../user/LoginDialog";
import { AuthStatusView } from "./AuthStatusView";
import { useAuthSessionOrchestrator } from "./AuthSessionOrchestrator";
import { setAuthRedirectTarget } from "./authRedirectTarget";

function isPublicPath(pathname: string): boolean {
  return pathname === "/register" || pathname.startsWith("/help");
}

/**
 * Gates the WorkUI application shell behind the local auth/session stage.
 *
 * Public registration and help paths retain their existing signed-out access.
 * All other routes render their outlet only after the WorkUI token session is
 * authenticated.
 */
export function AppAuthBoundary() {
  const location = useLocation();
  const authContext = useContext(AuthContext);
  const { beginLogin, stage } = useAuthSessionOrchestrator();
  const returnTo = `${location.pathname}${location.search}${location.hash}`;
  const loginRequested = new URLSearchParams(location.search).has("login");
  const publicPath = isPublicPath(location.pathname) && !loginRequested;

  useEffect(() => {
    if (publicPath || stage.status !== "signed-out" || stage.mode !== "auth0") {
      return;
    }

    setAuthRedirectTarget(returnTo);
    void beginLogin(returnTo);
  }, [beginLogin, publicPath, returnTo, stage]);

  if (publicPath) {
    return <Outlet />;
  }

  if (stage.status === "signed-out" && stage.mode === "legacy") {
    if (authContext.userIsAuthenticated) {
      return <Outlet />;
    }

    return (
      <LoginDialog
        checkTokenIsValid={authContext.checkTokenIsValid}
        onAuthenticate={authContext.setUserIsAuthenticated}
        show
      />
    );
  }

  if (stage.status === "signed-out" && stage.mode === "auth0") {
    return <AuthStatusView status="redirecting-to-login" />;
  }

  if (stage.status === "error") {
    return (
      <AuthStatusView
        message={stage.message}
        onRetry={() => void beginLogin(returnTo)}
        reason={stage.reason}
        status="error"
      />
    );
  }

  if (stage.status === "logging-out") {
    return <AuthStatusView status="logging-out" />;
  }

  if (stage.status === "authenticated") {
    return <Outlet />;
  }

  return <AuthStatusView status="authenticating" />;
}
